import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Settings2, BookOpen, ConciergeBell, CalendarCheck,
  Megaphone, CreditCard, LogOut, BarChart3, Gift, ExternalLink, Copy, Check, CalendarClock, QrCode, MessageSquare,
  MapPinned, UtensilsCrossed, ShoppingBag, Users, ShieldAlert, ServerCog, Gauge, BedDouble, Rss, Lock, Menu,
  Armchair, ClipboardCheck, SprayCan, Star,
} from "lucide-react";
import { venueTerms, featureKeys } from "@/lib/venues";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { LockedModule } from "@/components/LockedModule";
import { InstallAppButton } from "@/components/InstallAppButton";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

const links = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Overview", end: true },
  { to: "/dashboard/analytics", icon: BarChart3, label: "Analytics", feature: "analytics" },
  { to: "/dashboard/hub", icon: Settings2, label: "Hub Settings" },
  { to: "/dashboard/guests", icon: Users, label: "Guests", feature: "guest_accounts", term: "guests" },
  { to: "/dashboard/rooms", icon: BedDouble, label: "Rooms", feature: "rooms", term: "rooms" },
  { to: "/dashboard/directory", icon: BookOpen, label: "Directory & Guides", feature: "directory" },
  { to: "/dashboard/nearby", icon: MapPinned, label: "Explore Nearby", feature: "nearby_map" },
  { to: "/dashboard/services", icon: ConciergeBell, label: "Services", feature: "services" },
  { to: "/dashboard/schedule", icon: CalendarClock, label: "Schedule", feature: "schedule" },
  { to: "/dashboard/menu", icon: UtensilsCrossed, label: "Room Service Menu", feature: ["menu", "table_ordering"], term: "menu" },
  { to: "/dashboard/upgrades", icon: Gift, label: "Stay Upgrades", feature: "upgrades" },
  { to: "/dashboard/orders", icon: ShoppingBag, label: "Orders", feature: ["menu", "table_ordering"] },
  { to: "/dashboard/tables", icon: Armchair, label: "Table Reservations", feature: "table_reservations" },
  { to: "/dashboard/checkins", icon: ClipboardCheck, label: "Online Check-in", feature: "online_checkin" },
  { to: "/dashboard/housekeeping", icon: SprayCan, label: "Housekeeping", feature: "housekeeping" },
  { to: "/dashboard/reviews", icon: Star, label: "Reviews", feature: "review_booster" },
  { to: "/dashboard/bookings", icon: CalendarCheck, label: "Bookings", feature: "bookings" },
  { to: "/dashboard/channels", icon: Rss, label: "Channels", feature: "channels" },
  { to: "/dashboard/complaints", icon: ShieldAlert, label: "Complaints", feature: "complaints" },
  { to: "/dashboard/messages", icon: MessageSquare, label: "Messages", badgeKey: "messages", feature: "messages" },
  { to: "/dashboard/announcements", icon: Megaphone, label: "Announcements", feature: "announcements" },
  { to: "/dashboard/qr", icon: QrCode, label: "QR Kit", feature: "qr_kit" },
  { to: "/dashboard/billing", icon: CreditCard, label: "Billing" },
];

const BEEP = "data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=";

const isLocked = (l, features) => {
  const keys = featureKeys(l.feature);
  return !!(keys.length && features && !keys.some((k) => features[k]));
};

function NavItems({ items, features, unread, onNavigate, terms }) {
  return items.map((l) => {
    const locked = isLocked(l, features);
    const label = (l.term && terms?.[l.term]) || l.label;
    return (
      <NavLink
        key={l.to}
        to={l.to}
        end={l.end}
        onClick={onNavigate}
        data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
        data-locked={locked ? "true" : "false"}
        className={({ isActive }) =>
          `flex items-center gap-3 px-4 py-2.5 rounded-md text-sm transition-colors ${
            isActive ? "bg-gold text-black" : locked ? "text-cream-muted opacity-50 hover:opacity-80 hover:bg-white/5" : "text-cream-muted hover:text-cream hover:bg-white/5"
          }`
        }
      >
        <l.icon className="w-4 h-4" strokeWidth={1.5} />
        <span className="flex-1">{label}</span>
        {locked && <Lock className="w-3.5 h-3.5 opacity-70" data-testid={`nav-lock-${featureKeys(l.feature)[0]}`} />}
        {!locked && l.badgeKey === "messages" && unread > 0 && (
          <span data-testid="messages-unread-badge" className="ml-auto bg-red-500 text-white text-[10px] rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">{unread}</span>
        )}
      </NavLink>
    );
  });
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [biz, setBiz] = useState(null);
  const [features, setFeatures] = useState(null);
  const [featureInfo, setFeatureInfo] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const refreshFeatures = () => api.get("/business/features").then((r) => { setFeatures(r.data.features); setFeatureInfo(r.data); }).catch(() => {});
  const [copied, setCopied] = useState(false);
  const [unread, setUnread] = useState(0);
  const prevUnread = useRef(0);

  useEffect(() => {
    api.get("/business").then((r) => setBiz(r.data)).catch(() => {});
    refreshFeatures();
  }, []);

  const messagesOn = !!features?.messages;
  useEffect(() => {
    if (!messagesOn) { return; }
    const poll = () => api.get("/messages").then((r) => {
      const total = r.data.reduce((s, t) => s + (t.unread || 0), 0);
      setUnread(total);
      if (total > prevUnread.current && prevUnread.current !== null) {
        try { new Audio(BEEP).play().catch(() => {}); } catch (e) {}
        if (total > 0) toast.info(`New guest message (${total} unread)`);
      }
      prevUnread.current = total;
    }).catch(() => {});
    poll();
    const t = setInterval(poll, 7000);
    return () => clearInterval(t);
  }, [messagesOn]);

  const hidden = featureInfo?.hidden || [];
  const terms = venueTerms(featureInfo?.venue_type);
  const visibleLinks = links.filter((l) => { const keys = featureKeys(l.feature); return !keys.length || !keys.every((k) => hidden.includes(k)); });
  const navItems = [...visibleLinks, ...(user?.is_super_admin ? [{ to: "/dashboard/smtp", icon: ServerCog, label: "Platform SMTP" }, { to: "/admin", icon: Gauge, label: "Platform Admin" }] : [])];
  const lockedLink = features && links.find((l) => isLocked(l, features) && (location.pathname === l.to || location.pathname.startsWith(`${l.to}/`)));
  const lockedKey = lockedLink && featureKeys(lockedLink.feature)[0];

  const hubUrl = biz ? `${window.location.origin}/h/${biz.slug}` : "";

  const impersonating = typeof window !== "undefined" ? localStorage.getItem("grh_impersonating") : null;

  useEffect(() => {
    document.title = biz?.name ? `${biz.name} · Dashboard` : "Dashboard · Guest Relations Hub";
  }, [biz]);
  const exitImpersonation = () => {
    const adminToken = localStorage.getItem("grh_admin_token");
    if (adminToken) localStorage.setItem("grh_token", adminToken);
    localStorage.removeItem("grh_admin_token");
    localStorage.removeItem("grh_impersonating");
    window.location.href = "/admin";
  };

  const copyLink = () => {
    navigator.clipboard.writeText(hubUrl);
    setCopied(true);
    toast.success("Hub link copied");
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className="w-64 bg-ink text-cream flex-col hidden lg:flex fixed inset-y-0">
        <div className="p-6 border-b border-ink">
          <span className="font-serif text-lg">Guest Relations <span className="text-gold">Hub</span></span>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          <NavItems items={navItems} features={features} unread={unread} terms={terms} />
        </nav>
        <div className="p-4 border-t border-ink">
          <div className="text-xs text-cream-muted mb-1 truncate">{user?.name}</div>
          <div className="text-xs text-cream-muted mb-3 truncate">{user?.email}</div>
          <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn" className="flex items-center gap-2 text-sm text-cream-muted hover:text-cream transition-colors">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 lg:ml-64">
        {impersonating && (
          <div className="bg-amber-500 text-black text-sm px-6 py-2 flex items-center justify-between gap-3" data-testid="impersonation-banner">
            <span>Viewing as <strong>{impersonating}</strong> — changes you make affect this client's hub.</span>
            <button onClick={exitImpersonation} data-testid="exit-impersonation-btn" className="underline font-medium whitespace-nowrap hover:opacity-80">Exit to admin</button>
          </div>
        )}
        <header className="h-16 border-b border-border bg-card flex items-center justify-between px-4 sm:px-6 sticky top-0 z-30 gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <button onClick={() => setMobileOpen(true)} data-testid="mobile-nav-toggle" className="lg:hidden p-2 -ml-2 rounded-md hover:bg-secondary"><Menu className="w-5 h-5" /></button>
            <h2 className="font-serif text-lg text-foreground truncate">{biz?.name || "Dashboard"}</h2>
          </div>
          {biz && (
            <div className="flex items-center gap-2">
              <InstallAppButton testId="dashboard-install-btn" className="flex items-center gap-2 text-xs text-muted-foreground border border-border rounded-md px-3 py-2 hover:bg-secondary transition-colors" label="Install" />
              <button onClick={copyLink} data-testid="copy-hub-link" className="flex items-center gap-2 text-xs text-muted-foreground border border-border rounded-md px-3 py-2 hover:bg-secondary transition-colors">
                {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                <span className="hidden sm:inline">/h/{biz.slug}</span>
              </button>
              <a href={hubUrl} target="_blank" rel="noreferrer" data-testid="view-hub-link" className="flex items-center gap-2 text-xs bg-primary text-primary-foreground rounded-md px-3 py-2 hover:opacity-90 transition-opacity">
                <ExternalLink className="w-3.5 h-3.5" /> View hub
              </a>
            </div>
          )}
        </header>
        <div className="p-4 sm:p-6 max-w-6xl">
          {lockedLink ? (
            <LockedModule label={featureInfo?.labels?.[lockedKey] || lockedLink.label} plan={featureInfo?.plan} includedIn={featureInfo?.included_in?.[lockedKey] || []} />
          ) : (
            <Outlet context={{ biz, setBiz, features, featureInfo, refreshFeatures }} />
          )}
        </div>
      </main>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="bg-ink text-cream border-ink w-72 p-0 flex flex-col" data-testid="mobile-nav">
          <div className="p-6 border-b border-white/10"><SheetTitle className="font-serif text-lg text-cream">Guest Relations <span className="text-gold">Hub</span></SheetTitle></div>
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            <NavItems items={navItems} features={features} unread={unread} terms={terms} onNavigate={() => setMobileOpen(false)} />
          </nav>
          <button onClick={() => { logout(); navigate("/"); }} data-testid="mobile-logout-btn" className="m-4 flex items-center gap-2 text-sm text-cream-muted hover:text-cream transition-colors"><LogOut className="w-4 h-4" /> Sign out</button>
        </SheetContent>
      </Sheet>
    </div>
  );
}
